"use client";

/**
 * Lightweight app-wide toasts. `useToast()` returns { success, error, info };
 * calls stack a transient, auto-dismissing notification bottom-right. The
 * viewport is a polite live region so screen readers announce them; errors
 * escalate to role="alert". No dependency — a small context + timers.
 */

import { CheckCircle2, Info, X, XCircle } from "lucide-react";
import { createContext, useCallback, useContext, useRef, useState } from "react";

type Variant = "success" | "error" | "info";
interface Toast {
  id: number;
  message: string;
  variant: Variant;
}

interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const VARIANT_STYLE: Record<Variant, string> = {
  success: "border-ok/40 bg-ok-soft text-ok",
  error: "border-danger/40 bg-danger-soft text-danger",
  info: "border-accent-line bg-accent-soft text-accent",
};
const VARIANT_ICON = { success: CheckCircle2, error: XCircle, info: Info };

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (variant: Variant, message: string) => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, message, variant }]);
      // Errors linger a little longer than confirmations
      setTimeout(() => dismiss(id), variant === "error" ? 7000 : 4000);
    },
    [dismiss],
  );

  const api = useRef<ToastApi>({
    success: (m) => push("success", m),
    error: (m) => push("error", m),
    info: (m) => push("info", m),
  });
  // Keep closures current (push identity is stable via useCallback deps)
  api.current = {
    success: (m) => push("success", m),
    error: (m) => push("error", m),
    info: (m) => push("info", m),
  };

  return (
    <ToastContext.Provider value={api.current}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
      >
        {toasts.map((t) => {
          const Icon = VARIANT_ICON[t.variant];
          return (
            <div
              key={t.id}
              role={t.variant === "error" ? "alert" : "status"}
              className={`pointer-events-auto flex items-start gap-2.5 rounded-lg border px-3.5 py-2.5 text-sm shadow-lg ${VARIANT_STYLE[t.variant]}`}
            >
              <Icon className="mt-0.5 h-4 w-4 shrink-0" />
              <span className="flex-1">{t.message}</span>
              <button
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss"
                className="shrink-0 rounded p-0.5 opacity-70 transition-opacity hover:opacity-100"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

/** Toast API. Returns no-op stubs if used outside the provider (SSR / tests),
 * so callers never need to null-check. */
export function useToast(): ToastApi {
  return useContext(ToastContext) ?? { success: () => {}, error: () => {}, info: () => {} };
}
